// client/src/hooks/useFetch.js
import { useState, useEffect } from 'react';

// Same-origin path to our function. Lives here, not in App.js, because
// every fetch in the app uses this prefix — it's a config concern, not
// a per-screen concern. If the deployment URL ever changes, we change
// it in one place.
// TODO: env-ify once we set up the GitHub repo. Hardcoded to dev URL for now.
// Production URL will differ when we deploy to production environment.
const API_BASE = 'https://server-dev.bightyouthsports.com/server/ysa-api';

/**
 * Fetches a path under our API and returns { data, loading, error }.
 *
 * Handles three things every endpoint shares:
 *   1. HTTP-level failures (non-2xx responses)
 *   2. Our envelope shape ({ success: true, data } vs { success: false, error })
 *   3. The loading/error/success state machine
 *
 * Pass `null` or `undefined` as the path to skip fetching — useful when
 * waiting on a parent to resolve an id (e.g. `useFetch(teamId ?
 * `/teams/${teamId}/schedule` : null)`). The hook stays idle until a
 * real path arrives.
 *
 * Usage:
 *   const { data, loading, error } = useFetch('/me');
 *   const { data, loading, error } = useFetch(`/teams/${teamId}`);
 *   const { data, loading, error } = useFetch(teamId ? `/teams/${teamId}` : null);
 */
export function useFetch(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Start "loading" only if we have a real path to fetch. When callers
  // pass null (waiting on a parent id), we shouldn't show a spinner —
  // there's nothing to wait on yet.
  const [loading, setLoading] = useState(path != null);

  useEffect(() => {
    // Bail out early when the caller signals "not ready yet" with a
    // null path. Reset to a clean idle state so any stale data from a
    // previous path doesn't linger.
    if (path == null) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    // `cancelled` is a closure-scoped flag for the race condition where
    // the component unmounts (or the path prop changes) before this
    // fetch resolves. Without it, we'd call setState on a stale effect,
    // which React warns about and can cause subtle bugs in real apps.
    let cancelled = false;

    // Reset state every time the path changes — if a parent switches us
    // from /teams/1 to /teams/2, we want to show "loading" again, not
    // briefly flash the old data.
    setLoading(true);
    setError(null);

    async function run() {
      try {
        const res = await fetch(`${API_BASE}${path}`, {
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }

        const body = await res.json();

        if (!body.success) {
          throw new Error(body.error || 'Unknown server error');
        }

        // Only commit state if this effect's run is still the active one.
        if (!cancelled) setData(body.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    // The cleanup function. React calls this when:
    //   (a) the component unmounts, or
    //   (b) the effect is about to re-run (because `path` changed).
    // Either way, we flip the flag so the in-flight fetch's setState
    // calls become no-ops.
    return () => {
      cancelled = true;
    };
  }, [path]);

  return { data, loading, error };
}